import React, { useEffect, useState } from "react";
import saleService from "../../Services/saleService";
import DoctorSalesModal from "../../Components/DoctorSalesModal";

const SaleByReceptionist = () => {
  const [searchData, setSearchData] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [doctorSales, setDoctorSales] = useState([]);

  useEffect(() => {
    const getPSales = async () => {
      try {
        setLoading(true);
        const response = await saleService.fetchAllPSales();
        const groupedDoctors = response.sales.reduce((acc, sale) => {
          if (!acc[sale.receptionist_name]) {
            acc[sale.receptionist_name] = {
              receptionist_name: sale.receptionist_name,
              receptionist_email: sale.receptionist_email,
              sales: [],
            };
          }
          acc[sale.receptionist_name].sales.push(sale);
          return acc;
        }, {});
        setDoctors(Object.values(groupedDoctors));
      } catch (error) {
        console.error("Error fetching sales:", error);
      } finally {
        setLoading(false);
      }
    };
    getPSales();
  }, []);

  useEffect(() => {
    const result = (doctors || []).filter((item) =>
      item?.receptionist_name
        ?.toLowerCase()
        .includes((searchData || "").toLowerCase()),
    );
    setFilteredData(result);
  }, [searchData, doctors]);

  const colWidths = ["70px", "180px", "220px", "120px"];
  const ColGroup = () => (
    <colgroup>
      {colWidths.map((w, i) => (
        <col key={i} style={{ width: w, minWidth: w }} />
      ))}
    </colgroup>
  );

  return (
    <div className="p-3 sm:p-4 font-sans">
      {/* ── Header ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs font-medium tracking-widest uppercase text-gray-500">
            Sales
          </p>
          <p className="text-xs text-gray-400 mt-0.5">
            {filteredData.length || 0} records found
          </p>
        </div>
      </div>

      {/* ── Search ── */}
      <div className="relative mb-4">
        <svg
          className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z"
          />
        </svg>
        <input
          type="search"
          placeholder="Search by receptionist name..."
          value={searchData}
          onChange={(e) => setSearchData(e.target.value)}
          className="w-full sm:w-72 pl-9 pr-4 py-3 text-xs border border-gray-200 rounded-lg bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-100"
        />
      </div>

      {/* ══ MOBILE CARDS ══ */}
      <div className="block sm:hidden space-y-2">
        {loading ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            Loading...
          </div>
        ) : filteredData.length === 0 ? (
          <div className="text-center py-10 text-gray-400 text-xs">
            No records found
          </div>
        ) : (
          filteredData.map((doctor, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-xl bg-white px-3 py-3"
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-[11px] font-semibold text-gray-600 uppercase flex-shrink-0">
                    {doctor?.receptionist_name?.charAt(0) || "R"}
                  </div>
                  <div>
                    <p className="text-[12px] font-semibold text-gray-800">
                      {doctor.receptionist_name}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {doctor.receptionist_email}
                    </p>
                  </div>
                </div>
                <button
                  className="text-blue-500 font-medium text-[12px] hover:underline"
                  onClick={() => {
                    setSelectedDoctor(doctor.receptionist_name);
                    setDoctorSales(doctor.sales);
                  }}
                >
                  View
                </button>
              </div>
              <div className="flex justify-between py-1.5 border-t border-gray-100">
                <span className="text-[11px] text-gray-400">Sales</span>
                <span className="text-[11px] text-gray-700">
                  {doctor.sales.length} record(s)
                </span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* ══ DESKTOP TABLE ══ */}
      <div className="hidden sm:block border rounded-xl overflow-hidden">
        <div className="overflow-y-auto" style={{ maxHeight: "520px" }}>
          <table
            className="w-full text-sm"
            style={{
              tableLayout: "fixed",
              borderCollapse: "separate",
              borderSpacing: 0,
            }}
          >
            <ColGroup />
            <thead>
              <tr>
                {[
                  { label: "Sr No." },
                  { label: "Receptionist" },
                  { label: "Email Address" },
                  { label: "Action" },
                ].map(({ label }, i) => (
                  <th
                    key={label}
                    className={`sticky top-0 z-20 bg-[#1F2937] text-left px-4 py-2.5 text-[11px] text-gray-100 uppercase font-medium ${
                      i < 3 ? "border-r border-white/10" : ""
                    }`}
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    Loading...
                  </td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="text-center py-10 text-gray-400 text-[12px]"
                  >
                    No records found
                  </td>
                </tr>
              ) : (
                filteredData.map((doctor, index) => (
                  <tr
                    key={index}
                    className={`border-b border-gray-100 hover:bg-green-50 transition-colors ${
                      index % 2 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <td className="px-4 py-2.5 text-[12px] font-semibold text-gray-500 border-r border-gray-100">
                      {index + 1}
                    </td>
                    <td className="px-4 py-2.5 border-r border-gray-100">
                      <div className="flex items-center gap-2">
                        {/* <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-semibold text-gray-600 uppercase flex-shrink-0">
                          {doctor?.receptionist_name?.charAt(0) || "R"}
                        </div> */}
                        <span className="text-[12px] font-medium text-gray-800 truncate">
                          {doctor.receptionist_name}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-[12px] text-gray-600 border-r border-gray-100 truncate">
                      {doctor.receptionist_email}
                    </td>
                    <td className="px-4 py-2.5 text-[12px]">
                      <button
                        className="text-[11px] font-medium text-white bg-[#1F2937] px-2.5 py-1 rounded-lg hover:bg-[#111827] transition"
                        onClick={() => {
                          setSelectedDoctor(doctor.receptionist_name);
                          setDoctorSales(doctor.sales);
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan={4}
                  className="sticky bottom-0 z-20 bg-[#1F2937] px-4 py-2.5 text-[12px] font-medium text-gray-100 border-t border-white/10"
                >
                  {filteredData?.length || 0} total records
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {selectedDoctor && (
        <DoctorSalesModal
          doctor={selectedDoctor}
          sales={doctorSales}
          onClose={() => setSelectedDoctor(null)}
        />
      )}
    </div>
  );
};

export default SaleByReceptionist;
